import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { Button, ButtonVariants, Modal, rem } from '@triumph/design-system';
import { debounce } from 'es-toolkit';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import {
    FACTORING_LOADPAY,
    ProductNames,
} from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import {
    useGenerateRecoveryLinkMutation,
    useSubmitNeedsMoreInfoMutation,
} from '@/components/shared/api/mutations/applicant';

import { ModalContainer } from './ApplicantStatus.StyledComponents';

const LOADING_PAGE_TITLE = 'Opening application…';

// Modern (non-`document.write`) approach: about:blank already parses a valid
// <html><head><body> skeleton, so we just mutate title + body.innerHTML in
// the popup. innerHTML is safe here because the content is a static literal.
const LOADING_PAGE_BODY_HTML = `
<style>
  html, body { height: 100%; margin: 0; }
  body {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 16px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #0a1e3a;
    background: #ffffff;
  }
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(31, 91, 214, 0.18);
    border-top-color: #1f5bd6;
    border-radius: 50%;
    animation: spin 800ms linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  p { margin: 0; font-size: 14px; color: #4a5568; }
</style>
<div class="spinner" role="status" aria-label="Loading"></div>
<p>Opening application for editing…</p>
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${rem(20)} ${rem(20)};
    gap: ${rem(16)};
    width: 100%;
`;

const SelectionWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const CodeWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const CodeLabel = styled.div`
    font-size: ${rem(14)};
    font-weight: 500;
    color: #555;
    margin-bottom: ${rem(4)};
    width: 100%;
`;

const CodeBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${rem(12)};
    padding: ${rem(12)} ${rem(16)};
    background-color: #f5f5f5;
    border: 1px solid #d0d0d0;
    border-radius: ${rem(4)};
    margin-bottom: ${rem(16)};
    width: 100%;
    box-sizing: border-box;
    position: relative;
`;

const CodeText = styled.div`
    font-size: ${rem(20)};
    font-weight: 600;
    color: #333;
    letter-spacing: ${rem(16)};
    font-family: 'Courier New', monospace;
`;

const CopyButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: ${rem(4)};
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    transition: color 0.2s;
    position: absolute;
    right: ${rem(16)};
    font-size: ${rem(18)};

    &:hover:not(:disabled) {
        color: #333;
    }

    &:active:not(:disabled) {
        transform: scale(0.95);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    i {
        font-size: ${rem(18)};
    }
`;

const ButtonWrapper = styled.div`
    margin-top: ${rem(4)};
    width: 100%;

    button {
        width: 100%;
    }
`;

const MultiSelectWrapper = styled.div`
    width: 100%;
    margin-bottom: ${rem(16)};

    label {
        font-size: ${rem(16)};
        font-weight: 500;
        margin-bottom: ${rem(8)};
        display: block;
    }

    .p-multiselect {
        width: 100%;
        font-size: ${rem(14)};

        .p-multiselect-label {
            font-size: ${rem(14)};
            padding: ${rem(10)} ${rem(12)};
        }

        .p-multiselect-trigger {
            width: ${rem(40)};
        }

        .p-multiselect-token {
            font-size: ${rem(13)};
            padding: ${rem(4)} ${rem(8)};
        }

        .p-multiselect-token-label {
            font-size: ${rem(13)};
        }

        .p-multiselect-token-icon {
            font-size: ${rem(14)};
            width: ${rem(14)};
            height: ${rem(14)};
            margin-left: ${rem(6)};
        }
    }
`;

export const EditModal = ({
    showModal,
    setShowModal,
    defaultProduct,
    initialRecoveryLink,
    initialPasscode,
    getHasEverBeenApproved,
}: {
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    defaultProduct: string;
    initialRecoveryLink: string;
    initialPasscode: string;
    getHasEverBeenApproved: (product: ProductNames) => boolean;
}) => {
    const { applicantId, applicantProductNames } = useApplicantContext();
    const currentUserEmail = useCurrentUserEmail();
    const { isSafeEmailViewsEnabled } = useFeatureFlags();

    const [selectedProducts, setSelectedProducts] = useState<ProductNames[]>([]);
    const [recoveryLink, setRecoveryLink] = useState('');
    const [passcode, setPasscode] = useState('');
    const [hasSubmittedNMI, setHasSubmittedNMI] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);

    const initialProductsRef = useRef<ProductNames[]>([]);
    const lastGeneratedProductsRef = useRef<ProductNames[]>([]);
    const requestIdRef = useRef<number>(0);

    // Mutations
    const { mutateAsync: submitNeedsMoreInfo, isPending: isSubmitNeedsMoreInfoPending } =
        useSubmitNeedsMoreInfoMutation();
    const { mutateAsync: generateRecoveryLink } = useGenerateRecoveryLinkMutation();

    // Debounced function for generating recovery link (500ms debounce)
    const debouncedGenerateRecoveryLink = useMemo(
        () =>
            debounce(async (products: ProductNames[], appId: string, currentRequestId: number) => {
                try {
                    setIsGeneratingLink(true);

                    const response = await generateRecoveryLink({
                        applicantId: appId,
                        products,
                    });

                    // Only update state if this is still the most recent request
                    if (currentRequestId === requestIdRef.current) {
                        setRecoveryLink(response.recoveryUrl);
                        setPasscode(response.passcode || '');
                        // Reset NMI submission flag when new link is generated
                        setHasSubmittedNMI(false);

                        // Update lastGeneratedProductsRef to track what we just generated
                        lastGeneratedProductsRef.current = [...products];
                    }
                } catch (error) {
                    // Only log error if this is still the most recent request
                    if (currentRequestId === requestIdRef.current) {
                        console.error('Failed to generate recovery link:', error);
                    }
                } finally {
                    // Only clear loading state if this is still the most recent request
                    if (currentRequestId === requestIdRef.current) {
                        setIsGeneratingLink(false);
                    }
                }
            }, 500),
        [generateRecoveryLink],
    );

    // Reset state when modal opens
    useEffect(() => {
        if (showModal) {
            let initialProducts: ProductNames[] = [];

            // Handle FACTORING_LOADPAY by splitting into both products
            if (defaultProduct === FACTORING_LOADPAY) {
                initialProducts = [ProductNames.FACTORING, ProductNames.LOADPAY];
                setSelectedProducts(initialProducts);
            } else if (defaultProduct) {
                // Validate that defaultProduct is a valid ProductNames enum value
                const validProductNames = Object.values(ProductNames) as string[];
                if (validProductNames.includes(defaultProduct)) {
                    initialProducts = [defaultProduct as ProductNames];
                    setSelectedProducts(initialProducts);
                } else {
                    console.warn(
                        `Invalid defaultProduct value: "${defaultProduct}". Expected one of: ${validProductNames.join(', ')}, or "${FACTORING_LOADPAY}"`,
                    );
                    setSelectedProducts([]);
                }
            } else {
                setSelectedProducts([]);
            }

            // Store the initial products for comparison
            initialProductsRef.current = initialProducts;
            // Initialize lastGeneratedProductsRef with initial products since we have a pre-generated link
            lastGeneratedProductsRef.current = initialProducts;

            // Use the initial recovery link and passcode passed from parent
            setRecoveryLink(initialRecoveryLink);
            setPasscode(initialPasscode);
            setHasSubmittedNMI(false);
            setCopied(false);
        }
    }, [showModal, defaultProduct, initialRecoveryLink, initialPasscode]);

    // Debounced recovery link generation when products change
    useEffect(() => {
        // Don't run if modal is not shown or no products selected
        if (!showModal || selectedProducts.length === 0 || !applicantId) {
            return;
        }

        // Check if products have actually changed from last generation
        const productsChanged =
            lastGeneratedProductsRef.current.length === 0 || // No products generated yet (shouldn't happen)
            selectedProducts.length !== lastGeneratedProductsRef.current.length ||
            !selectedProducts.every((p) => lastGeneratedProductsRef.current.includes(p));

        // Don't regenerate if products haven't changed from last generation
        // This prevents unnecessary API calls on initial modal open
        if (!productsChanged) {
            return;
        }

        // Increment request ID to invalidate any pending requests
        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;

        // Call the debounced function
        debouncedGenerateRecoveryLink(selectedProducts, applicantId, currentRequestId);

        // Cleanup function - cancel pending debounced calls
        return () => {
            debouncedGenerateRecoveryLink.cancel();
        };
    }, [selectedProducts, showModal, applicantId, debouncedGenerateRecoveryLink]);

    const handleCopyCode = async () => {
        if (passcode) {
            try {
                await navigator.clipboard.writeText(passcode);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (error) {
                console.error('Failed to copy code:', error);
            }
        }
    };

    const queryClient = useQueryClient();

    const productOptions = applicantProductNames
        .filter(
            (product) =>
                !(product === ProductNames.LOADPAY && getHasEverBeenApproved(product)),
        )
        .map((product) => {
            let label: string = product;
            if (product === ProductNames.FACTORING) {
                label = 'Factoring';
            } else if (product === ProductNames.LOADPAY) {
                label = 'LoadPay';
            }
            return {
                label,
                value: product,
            };
        });

    const handleClose = () => {
        setShowModal(false);
    };

    const handleOpenForEdit = async () => {
        if (!applicantId || !recoveryLink || isSubmitNeedsMoreInfoPending) return;

        // Build the redirect URL up front — it doesn't depend on the API call.
        const url = new URL(recoveryLink);
        url.searchParams.set('internalEdit', 'true');
        url.searchParams.set('orp_edit', currentUserEmail || '');
        const editLink = url.toString();

        // Open the tab synchronously so it inherits the click's user activation
        // and isn't treated as an unsolicited popup. If the await happens first,
        // Chrome's popup blocker rejects the later window.open call.
        const editWindow = window.open('about:blank', '_blank');
        if (editWindow && isSafeEmailViewsEnabled) editWindow.opener = null;
        // about:blank is same-origin with the opener, so we can render a tiny
        // loading page in it while the NMI request resolves — friendlier than
        // staring at a blank tab.
        if (editWindow?.document?.body) {
            editWindow.document.title = LOADING_PAGE_TITLE;
            editWindow.document.body.innerHTML = LOADING_PAGE_BODY_HTML;
        }

        const isNewRequest = !hasSubmittedNMI;

        try {
            if (isNewRequest) {
                await submitNeedsMoreInfo({
                    applicantId,
                    products: selectedProducts,
                    notes: `Editing internally...`,
                    docsOnly: false,
                    isInternal: true,
                });

                setHasSubmittedNMI(true);

                queryClient.invalidateQueries({
                    queryKey: ['applicant-activity', applicantId],
                });
            }

            if (editWindow) {
                editWindow.location.href = editLink;
            } else {
                // Popup blocker blocked even the about:blank open — fall back to
                // navigating the current tab so the reviewer can still proceed.
                window.location.assign(editLink);
            }

            if (isNewRequest) {
                await queryClient.invalidateQueries({ queryKey: ['applicant', applicantId] });
            }
        } catch (error) {
            // If the API call failed, close the placeholder tab so the reviewer
            // doesn't stare at about:blank.
            editWindow?.close();
            console.error('Failed to open application for edit:', error);
        }
    };

    return (
        <Modal
            bodyContent={
                <ModalContainer>
                    <ContentWrapper>
                        <SelectionWrapper>
                            <MultiSelectWrapper>
                                <label htmlFor="multiselect-product-edit">Select Product(s)</label>
                                <MultiSelect
                                    id="multiselect-product-edit"
                                    value={selectedProducts}
                                    options={productOptions}
                                    onChange={(e) => setSelectedProducts(e.value)}
                                    placeholder="Select Product(s)"
                                    display="chip"
                                    data-testid="multiselect-product-edit"
                                    showSelectAll={false}
                                    showClear={false}
                                    panelClassName="edit-modal-multiselect-panel"
                                />
                            </MultiSelectWrapper>
                        </SelectionWrapper>

                        <CodeWrapper>
                            <CodeLabel>Your code is</CodeLabel>
                            <CodeBox>
                                {isGeneratingLink ? (
                                    <ProgressSpinner
                                        style={{ width: '32px', height: '32px' }}
                                        strokeWidth="4"
                                    />
                                ) : (
                                    <>
                                        <CodeText>{passcode || '------'}</CodeText>
                                        <CopyButton
                                            onClick={handleCopyCode}
                                            title={copied ? 'Copied!' : 'Copy code'}
                                            data-testid="copy-code-button"
                                            disabled={!passcode}
                                        >
                                            <i className={copied ? 'pi pi-check' : 'pi pi-clone'} />
                                        </CopyButton>
                                    </>
                                )}
                            </CodeBox>
                            <ButtonWrapper>
                                <Button
                                    variant={ButtonVariants.primary}
                                    onClick={handleOpenForEdit}
                                    data-testid="open-for-edit-button"
                                    loading={isSubmitNeedsMoreInfoPending || isGeneratingLink}
                                    disabled={
                                        isSubmitNeedsMoreInfoPending ||
                                        isGeneratingLink ||
                                        !recoveryLink
                                    }
                                >
                                    OPEN FOR EDIT
                                </Button>
                            </ButtonWrapper>
                        </CodeWrapper>
                    </ContentWrapper>
                </ModalContainer>
            }
            id="edit-modal"
            headerContent="Internal Edit"
            onClose={handleClose}
            visible={showModal}
            modalMaxWidth="500px"
            bodyContentPadding="0"
        />
    );
};
